import type { RefObject } from 'react';
import { throttle } from 'lodash-es';
import { useLayoutEffect, useState, useCallback } from 'react';
import rafSchd from 'raf-schd';

export interface ResizeObserverResult {
  width: number;
  height: number;
}

export const useResizeObserver = (ref: RefObject<HTMLElement>): ResizeObserverResult => {
  const [dimensions, setDimensions] = useState([0, 0]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize: (entries: ResizeObserverEntry[]) => void = useCallback(
    throttle(
      (entries) => {
        if (!Array.isArray(entries)) {
          return;
        }

        const entry = entries[0];
        setDimensions([entry.contentRect.width, entry.contentRect.height]);
      },
      100,
      { trailing: true },
    ),
    [],
  );

  useLayoutEffect(() => {
    let didUnsubscribe = false;
    if (!ref.current) {
      return undefined;
    }

    let resizeObserver: ResizeObserver | null = new ResizeObserver(
      rafSchd((entries) => {
        if (!didUnsubscribe) {
          handleResize(entries);
        }
      }),
    );
    resizeObserver.observe(ref.current);

    return () => {
      didUnsubscribe = true;
      resizeObserver?.disconnect();
      resizeObserver = null;
    };
  }, [ref, handleResize]);

  return { width: dimensions[0], height: dimensions[1] };
};
